"use client"

import { ProseContainer, ProseTitle, ProseSubtitle, ProseSection, ProseBody } from "@/components/layout/prose-container"
import { Button } from "@/components/ui/button"
import { ArrowRight, Plus, FileText, Lightbulb, AlertCircle } from "lucide-react"
import Link from "next/link"
import { BrainSnapshot } from "@/components/brain/brain-snapshot"

interface BusinessBrainCenterSurfaceProps {
  state: "overview" | "scope" | "memory"
  scope?: { type: "domain" | "node"; slug: string; label: string }
  memory?: { id: string; title: string; subtype?: string; scopeSlug?: string }
  onOpenMemory: (id: string) => void
  onAddMemory: (scope: { type: "domain" | "node"; slug: string }) => void
  onPrefillDraft: (params: { field: string; suggestedValue: string }) => void
}

export function BusinessBrainCenterSurface({
  state,
  scope,
  memory,
  onOpenMemory,
  onAddMemory,
  onPrefillDraft,
}: BusinessBrainCenterSurfaceProps) {
  // Overview state - dynamic Brain Snapshot (Phase 2)
  if (state === "overview") {
    return (
      <ProseContainer>
        <BrainSnapshot />
      </ProseContainer>
    )
  }

  // Scope state - domain or node selected
  if (state === "scope" && scope) {
    const scopeLabel = scope.label.charAt(0).toUpperCase() + scope.label.slice(1).replace(/-/g, " ")

    return (
      <ProseContainer>
        <ProseTitle>{scopeLabel}</ProseTitle>
        <ProseSubtitle>What I know about {scopeLabel.toLowerCase()}</ProseSubtitle>

        <ProseSection>What I Know</ProseSection>
        <ProseBody>
          This section contains structured memories about your business's {scopeLabel.toLowerCase()}. Each memory has
          been captured from conversations, documents, or direct input.
        </ProseBody>

        {/* Placeholder - would show actual memories */}
        <div className="my-6 p-4 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">No memories yet</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Start a conversation with your advisor to capture knowledge about {scopeLabel.toLowerCase()}.
          </p>
        </div>

        <ProseSection>Gaps</ProseSection>
        <ProseBody>Your advisor has identified areas where more information would improve advice quality.</ProseBody>

        {/* Placeholder gap cards */}
        <div className="my-4 space-y-3">
          <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Missing context</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Tell me more about your {scopeLabel.toLowerCase()} to unlock better recommendations.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs bg-transparent"
                    onClick={() => onPrefillDraft({ field: scope.slug, suggestedValue: "" })}
                  >
                    Prefill draft →
                  </Button>
                  <Link href={`/advisor?new=1&from=brain&scope=domain:${scope.slug}`}>
                    <Button variant="ghost" size="sm" className="h-7 text-xs">
                      Discuss with Advisor
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ProseSection>Suggested Updates</ProseSection>
        <div className="my-4 p-4 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Lightbulb className="h-4 w-4" />
            <span className="text-sm font-medium">No suggestions yet</span>
          </div>
          <p className="text-sm text-muted-foreground">
            As you converse with your advisor, relevant updates will appear here.
          </p>
        </div>

        <div className="pt-4 flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-1.5 bg-transparent" onClick={() => onAddMemory(scope)}>
            <Plus className="h-3.5 w-3.5" />
            Add memory
          </Button>
        </div>
      </ProseContainer>
    )
  }

  // Memory focus state - specific memory selected
  if (state === "memory" && memory) {
    return (
      <ProseContainer>
        <ProseTitle>{memory.title}</ProseTitle>
        {memory.subtype && <ProseSubtitle>{memory.subtype}</ProseSubtitle>}

        <ProseSection>Purpose</ProseSection>
        <ProseBody>
          This memory helps your advisor understand a specific aspect of your business. It's used to ground
          recommendations and ensure advice is contextually relevant.
        </ProseBody>

        <ProseSection>Content</ProseSection>
        <div className="my-4 p-4 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
          <p className="text-sm text-muted-foreground italic">Memory content would appear here...</p>
        </div>

        <ProseSection>Examples of How This Shapes Advice</ProseSection>
        <ProseBody>
          When you ask about strategy or decisions, your advisor references this memory to provide guidance that fits
          your specific situation rather than generic recommendations.
        </ProseBody>

        <ProseSection>Validation Checklist</ProseSection>
        <div className="my-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 rounded border border-[var(--border)]" />
            <span>Is this still accurate?</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 rounded border border-[var(--border)]" />
            <span>Does this need updating?</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 rounded border border-[var(--border)]" />
            <span>Should this be archived?</span>
          </div>
        </div>

        <div className="pt-4 flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 bg-transparent"
            onClick={() => onPrefillDraft({ field: "memory", suggestedValue: memory.id })}
          >
            Edit memory
          </Button>
          <Link href={`/advisor?new=1&from=brain&scope=memory:${memory.id}`}>
            <Button variant="ghost" size="sm" className="gap-1.5">
              Discuss with Advisor
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
        </div>
      </ProseContainer>
    )
  }

  // Fallback
  return null
}
